import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchBrush } from "../lib/api";
import { useAccess } from "../context/AccessContext";
import { useCart } from "../context/CartContext";
import SEO from "../components/SEO";
import StructuredData, {
  breadcrumbSchema,
  productSchema,
  howToSchema,
  faqSchema,
} from "../components/StructuredData";

export default function BrushDetail() {
  const { id } = useParams();
  const [brush, setBrush] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { unlocked } = useAccess();
  const { addItem } = useCart();
  const [justAdded, setJustAdded] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchBrush(id)
      .then(setBrush)
      .catch(() => setError("Brush not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const addAndGo = (productId) => {
    addItem(productId);
    setJustAdded(productId);
    setTimeout(() => setJustAdded(null), 1200);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="brush-detail-loading">
        <div className="h-12 w-1/2 bg-[#F2EFEB] animate-pulse mb-6" />
        <div className="h-[420px] bg-[#F2EFEB] animate-pulse" />
      </div>
    );
  }
  if (error || !brush) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-testid="brush-detail-error">
        <div className="overline mb-3">404</div>
        <h1 className="font-serif-display text-4xl text-stone-900">We couldn&apos;t find that brush.</h1>
        <Link to="/catalog" className="inline-block mt-8 text-xs uppercase tracking-[0.22em] text-[#8C6A5D]">
          ← Back to the catalog
        </Link>
      </div>
    );
  }

  const isFreeSample = brush.id === "powder-brush";
  const canShowTutorials = unlocked || isFreeSample;

  const seoTitle = `${brush.name} — How to Use It`;
  const seoDesc = `${brush.tagline ? brush.tagline + " " : ""}${brush.what || ""} A clear, editorial guide to using a ${brush.name.toLowerCase()} — best for ${brush.best_for || brush.category}.`.trim().slice(0, 200);
  const seoImg = brush.image && brush.image.startsWith("http") ? brush.image : `https://makeupbrushbasics.com${brush.image || "/images/og-default.jpg"}`;

  return (
    <div data-testid="brush-detail" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={`/brush/${brush.id}`}
        image={seoImg}
      />
      <StructuredData
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Catalog", path: "/catalog" },
            { name: brush.name, path: `/brush/${brush.id}` },
          ]),
          productSchema(brush),
          howToSchema(brush),
          faqSchema(brush),
        ]}
      />
      <Link
        to="/catalog"
        data-testid="back-to-catalog"
        className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-stone-900"
      >
        ← The Catalog
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-8">
        <div className="lg:col-span-6">
          <div className="aspect-[4/5] bg-[#F2EFEB] overflow-hidden">
            <img src={brush.image} alt={brush.name} className="w-full h-full object-cover" data-testid="brush-image" />
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="overline mb-4">
            {brush.category} · {brush.skill}
            {isFreeSample && !unlocked && (
              <span data-testid="free-sample-badge" className="ml-3 inline-block bg-[#E8D5D1] text-stone-900 px-2 py-0.5 text-[10px] tracking-[0.22em]">
                Free Sample
              </span>
            )}
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight leading-[1.05]">
            {brush.name}
          </h1>
          <p className="mt-5 text-lg italic text-stone-600 font-light">{brush.tagline}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 border-t border-[#E5E0D8] pt-8">
            <div>
              <div className="overline mb-2">Bristle</div>
              <p className="text-sm text-stone-700 leading-relaxed">{brush.bristle}</p>
            </div>
            <div>
              <div className="overline mb-2">Best For</div>
              <p className="text-sm text-stone-700 leading-relaxed">{brush.best_for}</p>
            </div>
          </div>

          <div className="mt-12">
            <div className="overline mb-3">What it is</div>
            <p className="text-base text-stone-700 leading-relaxed">{brush.what}</p>
          </div>

          {/* GATED CONTENT */}
          {canShowTutorials ? (
            <>
              {isFreeSample && !unlocked && (
                <div data-testid="free-sample-banner" className="mt-10 bg-[#F2EFEB] border-l-2 border-[#8C6A5D] px-5 py-4">
                  <div className="overline mb-1">You&apos;re reading a free sample</div>
                  <p className="text-sm text-stone-700 leading-relaxed">
                    This is one of 30 brush tutorials. <Link to="/shop" className="underline">Unlock the other 29 →</Link>
                  </p>
                </div>
              )}
              <div className="mt-12">
                <div className="overline mb-4">How to use it</div>
                <ol className="space-y-5" data-testid="how-to-list">
                  {brush.how_to_use.map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="font-serif-display text-3xl text-[#8C6A5D] leading-none w-8 shrink-0">
                        {(i + 1).toString().padStart(2, "0")}
                      </span>
                      <p className="text-base text-stone-700 leading-relaxed">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-12">
                <div className="overline mb-4">Technique tips</div>
                <ul className="space-y-3">
                  {brush.technique_tips.map((t, i) => (
                    <li key={i} className="text-sm text-stone-700 leading-relaxed pl-5 border-l-2 border-[#E8D5D1]">
                      {t}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-12 mb-8 bg-[#F2EFEB] p-6">
                <div className="overline mb-2">Care</div>
                <p className="text-sm text-stone-700 leading-relaxed">{brush.care}</p>
              </div>
            </>
          ) : (
            <div data-testid="brush-paywall" className="mt-12 mb-8 bg-[#2C2A29] text-stone-100 p-8 lg:p-10">
              <div className="overline mb-3" style={{ color: "#E8D5D1" }}>Tutorials are gated</div>
              <h3 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
                Unlock the technique.
              </h3>
              <p className="mt-4 text-sm text-stone-300 leading-relaxed max-w-md">
                Step-by-step how-to-use, technique tips, and care for all 30 brushes. One-time purchase, lifetime access — from any device.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#F9F7F3] text-stone-900 p-5 flex flex-col">
                  <div className="overline mb-2">Digital Only</div>
                  <div className="font-serif-display text-2xl">Tutorial Vault</div>
                  <p className="text-xs text-stone-500 mt-1 flex-1">Unlock all 30 tutorials forever.</p>
                  <div className="font-serif-display text-3xl mt-3">$29</div>
                  <button
                    data-testid="paywall-buy-tutorials"
                    onClick={() => addAndGo("tutorial-vault")}
                    className={`mt-4 text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors ${justAdded === "tutorial-vault" ? "bg-[#2C2A29] text-white" : "bg-[#8C6A5D] hover:bg-[#70544A] text-white"}`}
                  >
                    {justAdded === "tutorial-vault" ? "Added ✓" : "Unlock Tutorials"}
                  </button>
                </div>
                <div className="bg-[#F9F7F3] text-stone-900 p-5 flex flex-col">
                  <div className="overline mb-2">Brushes + Tutorials</div>
                  <div className="font-serif-display text-2xl">Complete 30-Piece Set</div>
                  <p className="text-xs text-stone-500 mt-1 flex-1">The full 30-brush kit plus tutorial access.</p>
                  <div className="font-serif-display text-3xl mt-3">$299</div>
                  <button
                    data-testid="paywall-buy-set"
                    onClick={() => addAndGo("set-complete-30")}
                    className={`mt-4 text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors ${justAdded === "set-complete-30" ? "bg-[#2C2A29] text-white" : "bg-[#F9F7F3] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB]"}`}
                  >
                    {justAdded === "set-complete-30" ? "Added ✓" : "Buy the Full Kit"}
                  </button>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3 text-xs text-stone-400">
                <Link to="/cart" className="underline hover:text-stone-200">View cart</Link>
                <span>·</span>
                <Link to="/shop" className="underline hover:text-stone-200">See all options</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
